import React, { useState, MouseEvent } from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';

import { Box, IconButton, Tooltip, ClickAwayListener } from '@mui/material';

import {
  AddOutlined,
  RestartAltOutlined,
  SaveAsOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';

import { useAIDocumentEditorContext } from '../../../../contexts/AIDocumentEditorContext';
import { useAIPromptCardStyles } from './styles';
import SaveTemplateIcon from '../../../../icons/DocumentTemplateIcon';

import EditCard from './EditCard';
import CardDetails from './CardDetails';
import { IPromptCard } from '@homeporter/types';
import { StyledSideTabs } from '../../../../components/StyledTabs';
import { StyledSideTab } from '../../../../components/StyledTabs';

const SectionCards = ({ viewTitle }: { viewTitle: string }) => {
  const {
    resetDocumentSection,
    documentsRef,
    saveTemplateButtonRef,
    documentIndex,
    documentSection,
    setDocumentSection,
    regenerateIndex,
    setRegenerateIndex,
    isGeneratingResponse,
    errorGeneratingResponse,
    selectedCard,
    setSelectedCard,
    setSaveEnabled,
    showGenerateReportPanel,
  } = useAIDocumentEditorContext();

  const styles = useAIPromptCardStyles();

  const [editCardIndex, setEditCardIndex] = useState<string>('');
  const [cardMenuOpen, setCardMenuOpen] = useState<boolean[]>([]);
  const [showHidden, setShowHidden] = useState(false);

  const [showEditPromptCard, setShowEditPromptCard] = useState(false);

  const handleClickAway = (event: Event) => {
    event.stopPropagation();
    if (!selectedCard) return;

    console.log('::::::::Section handleClickAway...');

    setSelectedCard(null);
    setEditCardIndex('');
    setRegenerateIndex(undefined);
  };

  const handleDrop = (result: DropResult) => {
    if (
      !documentsRef?.current ||
      !result.destination ||
      !documentSection?.cards
    )
      return;

    const cards = documentSection?.cards
      .filter((card) => showHidden || card.include)
      .sort((a, b) => a.sort - b.sort);

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    const sourceIndexValue = cards[sourceIndex].sort;
    const destIndexValue = cards[destIndex].sort;

    documentSection.cards.forEach((card) => {
      if (card.sort === sourceIndexValue) {
        card.sort = destIndexValue;
      } else if (card.sort === destIndexValue) {
        card.sort = sourceIndexValue;
      }
    });

    const updatedDocumentSection = { ...documentSection };
    documentsRef.current[documentIndex].sections[documentSection.index] =
      updatedDocumentSection;

    console.log('::::setDocument section 18...');
    setDocumentSection(updatedDocumentSection);
    setSaveEnabled(true);
  };

  return (
    <>
      <StyledSideTabs value={0} onChange={() => {}}>
        <StyledSideTab value={0} label={viewTitle} />
        {editCardIndex === '' && (
          <Box sx={styles.iconTray}>
            <Tooltip
              title={`${showHidden ? 'Exclude' : 'Include'} Hidden Cards`}>
              <IconButton
                onClick={() => setShowHidden(!showHidden)}
                sx={{
                  ...styles.trayButton,
                  marginLeft: '1rem',
                  marginRight: 'auto',
                  border: `.1rem solid ${showHidden ? '#d3d3d3' : 'orange'}`,
                }}>
                <VisibilityOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Card">
              <IconButton
                disabled={editCardIndex !== ''}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCard(null);
                  setShowEditPromptCard(true);
                  setEditCardIndex('-1');
                }}
                sx={{
                  ...styles.trayButton,
                  borderRadius: '1rem',
                  border: '0.1rem solid #d3d3d3',
                }}>
                <AddOutlined sx={{ fontSize: '1.5rem' }} />
              </IconButton>
            </Tooltip>

            {documentSection?.cards && documentSection?.cards.length > 0 && (
              <>
                <Tooltip
                  title="Save template"
                  componentsProps={{ tooltip: { sx: { marginTop: '0' } } }}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      saveTemplateButtonRef.current?.();
                    }}
                    sx={styles.iconButton}>
                    <SaveTemplateIcon sx={{ fontSize: '1.5rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title="Reset Document"
                  componentsProps={{ tooltip: { sx: { marginTop: '0' } } }}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          'Are you sure you want to reset this document? You can exit the AI document editor without saving to undo.',
                        )
                      ) {
                        resetDocumentSection();
                      }
                    }}
                    sx={{ ...styles.iconButton, color: '#bbb' }}>
                    <RestartAltOutlined sx={{ fontSize: '1.5rem' }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        )}
      </StyledSideTabs>
      <Box
        id="top_box"
        sx={{
          width: '100%',
          height: 'calc(100% - 3rem)',
          backgroundColor: '#fff',
        }}>
        <ClickAwayListener onClickAway={handleClickAway}>
          <div
            id="inside_div"
            style={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              maxHeight: '100%',
              overflowY: 'auto',
            }}>
            <EditCard
              open={showEditPromptCard}
              setOpen={setShowEditPromptCard}
              editCardIndex={editCardIndex}
              setEditCardIndex={setEditCardIndex}
              showHidden={showHidden}
              setShowHidden={setShowHidden}
            />

            {editCardIndex === '' && documentSection && (
              <>
                {!showHidden ? (
                  <DragDropContext onDragEnd={handleDrop}>
                    <Droppable droppableId="cards">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}>
                          {documentSection.cards
                            .filter((card) => showHidden || card.include)
                            .sort((a, b) => a.sort - b.sort)
                            .map((card, index) => {
                              const originalIndex =
                                documentSection.cards.findIndex(
                                  (c) => c === card,
                                );
                              return (
                                <Draggable
                                  key={index}
                                  draggableId={`card-${index}`}
                                  index={index}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}>
                                      <CardDetails
                                        card={card as IPromptCard}
                                        index={originalIndex}
                                        selectedCard={selectedCard}
                                        styles={styles}
                                        showHidden={showHidden}
                                        documentSection={documentSection}
                                        setSelectedCard={setSelectedCard}
                                        setSaveEnabled={setSaveEnabled}
                                        isGeneratingResponse={
                                          isGeneratingResponse
                                        }
                                        errorGeneratingResponse={
                                          !!errorGeneratingResponse
                                        }
                                        regenerateIndex={regenerateIndex}
                                        setRegenerateIndex={setRegenerateIndex}
                                        setEditCardIndex={setEditCardIndex}
                                        cardMenuOpen={cardMenuOpen}
                                        setCardMenuOpen={setCardMenuOpen}
                                        documentsRef={documentsRef}
                                        documentIndex={documentIndex}
                                        setDocumentSection={setDocumentSection}
                                        showGenerateReportPanel={
                                          showGenerateReportPanel
                                        }
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                ) : (
                  <div
                    id="prompt-cards-list"
                    style={{
                      height: '100%',
                      overflowY: 'auto',
                      paddingBottom: '2rem',
                    }}>
                    {documentSection.cards
                      .filter((card) => showHidden || card.include)
                      .sort((a, b) => a.sort - b.sort)
                      .map((card) => {
                        const originalIndex = documentSection.cards.findIndex(
                          (c) => c === card,
                        );
                        return (
                          <CardDetails
                            key={originalIndex}
                            card={card as IPromptCard}
                            index={originalIndex}
                            selectedCard={selectedCard}
                            styles={styles}
                            showHidden={showHidden}
                            documentSection={documentSection}
                            setSelectedCard={setSelectedCard}
                            setSaveEnabled={setSaveEnabled}
                            isGeneratingResponse={isGeneratingResponse}
                            errorGeneratingResponse={!!errorGeneratingResponse}
                            regenerateIndex={regenerateIndex}
                            setRegenerateIndex={setRegenerateIndex}
                            setEditCardIndex={setEditCardIndex}
                            cardMenuOpen={cardMenuOpen}
                            setCardMenuOpen={setCardMenuOpen}
                            documentsRef={documentsRef}
                            documentIndex={documentIndex}
                            setDocumentSection={setDocumentSection}
                            showGenerateReportPanel={showGenerateReportPanel}
                          />
                        );
                      })}
                  </div>
                )}
              </>
            )}
          </div>
        </ClickAwayListener>
      </Box>
    </>
  );
};
export default SectionCards;
